import { useMemo, useState } from "Shared/haunted/CustomHooks";
import { html } from "lit-html";
import { isCultureEnabled, getSelectableCulturesInOrder, mapToCultureListItem } from "ComponentHelpers/CultureHelper";
import { useRef } from "haunted";
import { ref } from "Components/directives/ref";
import classNames from "classnames";
import { useReduxState } from "Shared/redux/useReduxState";
import { isTabletOrMobileResolution } from "Services/common";
import { DynamicSettings } from "Components/admin/DynamicSettings";

export interface Props {
	culture: string;
	dynamicSettings: DynamicSettings;
	isHeaderCollapsed: boolean;
	onOpen: () => void;
}

export interface CultureListItem {
	culture: string;
	name: string;
}

export const useCultureSelector = (props: Props) => {
	const [navitaireSettings] = useReduxState("navitaireSettings");
	const [userInfo] = useReduxState("userInfo");

	const listElem = useRef<HTMLDivElement>(undefined);

	const [isOpen, setIsOpen] = useState<boolean>(false);

	const cultureList: CultureListItem[] = useMemo(() => {
		const list = props.dynamicSettings.DynamicCultureNameSettings || [];

		const [selectedLanguage] = props.culture.toLowerCase().split("-");

		// Convert dynamic culture name settings to culture list items
		const newList = list.map((item) => {
			const name = selectedLanguage === "es" ? item?.Es : selectedLanguage === "en" ? item?.En : item?.Pt;
			return { culture: item?.Culture, name: name || "" };
		});

		return newList.filter((listItem) => isCultureEnabled(navitaireSettings?.cultureSettings, listItem.culture));
	}, [navitaireSettings?.cultureSettings]);

	const cultures = useMemo(
		() => getSelectableCulturesInOrder(navitaireSettings?.cultureSettings, cultureList, props.culture),
		[cultureList, props.culture, navitaireSettings?.cultureSettings]
	);

	const canSwitchCulture = useMemo(
		() =>
			navitaireSettings?.cultureSettings?.Cultures?.length &&
			!userInfo?.BancoEstado?.Category &&
			!userInfo?.ChileCompra?.IsMember &&
			!userInfo?.PeruCompra?.IsAdmin &&
			!userInfo?.PeruCompra?.IsMember,
		[navitaireSettings, userInfo]
	);

	const handleCountryListOpen = () => {
		if (!canSwitchCulture) return;

		if (isOpen) {
			setIsOpen(false);
		} else {
			setIsOpen(true);
			props.onOpen();
		}
	};

	const cultureTemplate = (culture: CultureListItem) => html`
		<img class=${classNames("flag", `flag-${culture.culture.substring(3)}`)} />
		<span class="w-full text-left md:max-w-fit"> ${culture.name}</span>
	`;

	const openCultureTemplate = () => {
		const selectedCulture = mapToCultureListItem(cultureList, props.culture);
		return cultureTemplate(selectedCulture);
	};

	const cultureOptionTemplate = (culture: CultureListItem) => {
		const [language, country] = culture.culture.toLowerCase().split("-");
		const url = `/ChangeLanguage?newCountryCode=${country}&newLanguagecode=${language}`;

		return html`<a href=${url}>${cultureTemplate(culture)}</a>`;
	};

	const cultureDropdownTemplate = () =>
		canSwitchCulture
			? html`
					<div
						ref=${ref(listElem)}
						style="max-height: ${isOpen ? `${listElem.current.scrollHeight}px` : "0"};"
						class=${classNames("cultures top-[59px]", {
							"lg:top-[90px]": !props.isHeaderCollapsed,
						})}
					>
						<ul>
							${cultures.map(cultureOptionTemplate)}
						</ul>
					</div>
			  `
			: "";

	const arrowTemplate = () =>
		canSwitchCulture
			? html`
					<span
						class=${classNames(
							"ml-1 border-x-[5px] border-b-0 border-t-[5px] border-solid border-x-transparent transform common-transition origin-[50%_30%] border-t-[#204071]",
							{
								"-rotate-180": isOpen,
								"rotate-0": !isOpen,
							}
						)}
					></span>
			  `
			: "";

	const openerTemplate = () => html`
		<a
			class=${classNames("w-full py-4", "md:max-w-[180px]", {
				"opened": isOpen,
				"cursor-pointer": canSwitchCulture,
				"pointer-events-none opacity-50 cursor-default": !canSwitchCulture,
			})}
		>
			${openCultureTemplate()} ${arrowTemplate()}
		</a>
		<!-- DEVNOTE: Invisible hover extension to keep dropdown open when cursor moves to the header border below -->
		<div class="absolute -bottom-1 left-0 right-0 hidden h-1 cursor-pointer bg-transparent md:block"></div>
	`;

	const htmlTemplate = () =>
		cultures.length > 0
			? html`
					<div
						class="dg-culture-dropdown-opener h-full common-transition md:w-[167px]"
						@click=${isTabletOrMobileResolution() ? handleCountryListOpen : null}
						@mouseenter=${!isTabletOrMobileResolution() ? handleCountryListOpen : null}
						@mouseleave=${!isTabletOrMobileResolution() ? handleCountryListOpen : null}
					>
						${openerTemplate()} ${cultureDropdownTemplate()}
					</div>
			  `
			: "";

	return {
		close: () => setIsOpen(false),
		htmlTemplate,
	};
};
